import React, { createContext, useContext, useReducer, ReactNode, useEffect, useRef } from 'react';
import { BASE_URL } from '../constants/constants';
import { tokenStorage } from '../utils/tokenStorage';
import { translateError } from '../utils/errorTranslations';

// Types
export type UserRole = 'ADMIN' | 'STAFF' | 'DOCTOR' | 'PATIENT';

export interface User {
  id?: number; // Optional - not returned by login, only by /me endpoint
  email: string;
  firstName: string;
  lastName: string;
  fullName: string;
  role: UserRole;
  active: boolean;
  lastLogin?: string;
  createdAt?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: User }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' };

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
}

// Initial state - check localStorage synchronously to prevent flash of unauthenticated state
const getInitialState = (): AuthState => {
  const hasTokens = tokenStorage.hasTokens();
  return {
    user: null,
    isAuthenticated: false,
    isLoading: hasTokens, // If tokens exist, we're loading (checking validity)
    error: null,
  };
};

const initialState: AuthState = getInitialState();

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// localStorage key used by tokenStorage for the access token.
const ACCESS_TOKEN_STORAGE_KEY = 'accessToken';

// Decode the Cognito subject (user id) from a JWT, or null if unreadable.
const subFromToken = (token: string | null): string | null => {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
    const payload = JSON.parse(atob(base64 + pad));
    return payload.sub ?? null;
  } catch {
    return null;
  }
};

// Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const hasCheckedAuth = useRef(false);

  // Check authentication status on app load
  useEffect(() => {
    // CRITICAL: Only check auth once per app session, not on every remount
    if (hasCheckedAuth.current) {
      return;
    }

    hasCheckedAuth.current = true;

    // Always check auth on mount - this will either:
    // 1. Restore the session from localStorage if tokens exist
    // 2. Or confirm logout if no tokens exist
    checkAuth();

    // Listen for unauthorized events from API
    const handleUnauthorized = () => {
      dispatch({ type: 'LOGOUT' });
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    // Single session per browser: react to auth changes made in OTHER tabs
    // (localStorage is shared). The `storage` event fires only in the other tabs,
    // never in the one that made the change.
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key !== ACCESS_TOKEN_STORAGE_KEY) return;
      if (!e.newValue) {
        // Signed out in another tab → sign out here too (this also drops the WebSocket).
        dispatch({ type: 'LOGOUT' });
        return;
      }
      const newSub = subFromToken(e.newValue);
      const oldSub = subFromToken(e.oldValue);
      if (newSub && newSub !== oldSub) {
        // A DIFFERENT user signed in in another tab. Re-boot this tab so its
        // identity (UI, API token, WebSocket) follows the single active session
        // and lands on that user's home — not a page meant for the previous role.
        window.location.href = '/';
      }
      // Same user (e.g. a background token refresh) → ignore.
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('storage', handleStorageChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await fetch(`${BASE_URL}api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Login failed');
      }

      // Parse response with tokens
      const data = await response.json();

      // Store JWT tokens
      tokenStorage.setTokens(
        data.accessToken,
        data.idToken,
        data.refreshToken
      );

      // Dispatch user data
      dispatch({ type: 'LOGIN_SUCCESS', payload: data.user });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: translateError(errorMessage)
      });
    }
  };

  const logout = async (): Promise<void> => {
    try {
      // Get access token for logout request
      const accessToken = tokenStorage.getAccessToken();

      if (accessToken) {
        // Call logout endpoint (optional - JWT logout is client-side)
        await fetch(`${BASE_URL}api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Close mobile sidebar if open
      document.body.classList.remove('slide-nav');
      document.documentElement.classList.remove('menu-opened');
      const overlay = document.querySelector('.sidebar-overlay');
      if (overlay) {
        overlay.classList.remove('opened');
      }

      // Clear JWT tokens from storage
      tokenStorage.clearTokens();
      // Clear local state
      dispatch({ type: 'LOGOUT' });
    }
  };

  const checkAuth = async (): Promise<void> => {
    const accessToken = tokenStorage.getAccessToken();

    if (!accessToken) {
      dispatch({ type: 'LOGOUT' });
      return;
    }

    // Set loading state while validating
    dispatch({ type: 'LOGIN_START' });

    try {
      // Verify token is still valid by calling /me endpoint
      const response = await fetch(`${BASE_URL}api/auth/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        dispatch({ type: 'LOGIN_SUCCESS', payload: userData });
      } else {
        // Token expired or invalid - clear tokens
        tokenStorage.clearTokens();
        dispatch({ type: 'LOGOUT' });
      }
    } catch (error) {
      // Network errors or other issues
      console.error('Auth check failed:', error);
      tokenStorage.clearTokens();
      dispatch({ type: 'LOGOUT' });
    }
  };

  const clearError = (): void => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    checkAuth,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
